import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useFilterStore } from './searchFilterPanel/FilterStoreProvider';
import useSortStore from './searchSort/SortStore';
import { CATEGORY_ALL } from '../discover/Discover';
import { decodeCategoryName } from './searchUtils';
import { FILTER_KEYS } from '../../constants/constants';

/**
 * 검색 페이지의 복잡한 상태 관리 및 비즈니스 로직을 처리하는 커스텀 훅
 * @returns {object} 검색 페이지 렌더링에 필요한 상태와 핸들러
 */
export function useSearchManager() {
    const { keyword, categoryName: partiallyEncodedCategoryName = CATEGORY_ALL.name } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const categoryName = decodeCategoryName(partiallyEncodedCategoryName);

    const filters = useFilterStore(state => state.filters);
    const initializeFilters = useFilterStore(state => state.initializeFilters);
    const updateFilter = useFilterStore(state => state.updateFilter);
    const clearAllFilters = useFilterStore(state => state.clearAllFilters);

    const selectedSort = useSortStore(state => state.selectedSort);
    const setSort = useSortStore(state => state.setSort);
    const initializeSort = useSortStore(state => state.initializeSort);

    // URL 파라미터 -> 상태 초기화
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const initialFilters = {
            [FILTER_KEYS.GENDER]: searchParams.get(FILTER_KEYS.GENDER) || '',
            categories: searchParams.getAll('category') || [],
            [FILTER_KEYS.SUBCATEGORY_IDS]: (() => {
                const idsParam = searchParams.get(FILTER_KEYS.SUBCATEGORY_IDS);
                if (!idsParam) return [];
                return idsParam.split(',').map(id => parseInt(id, 10)).filter(id => !isNaN(id));
            })(),
            [FILTER_KEYS.BRANDS]: (() => {
                const bParam = searchParams.get(FILTER_KEYS.BRANDS);
                if (bParam) return bParam.split(',');
                return searchParams.getAll('brand') || [];
            })(),
            [FILTER_KEYS.PRICE_MIN]: searchParams.get(FILTER_KEYS.PRICE_MIN) ? parseInt(searchParams.get(FILTER_KEYS.PRICE_MIN)) : null,
            [FILTER_KEYS.PRICE_MAX]: searchParams.get(FILTER_KEYS.PRICE_MAX) ? parseInt(searchParams.get(FILTER_KEYS.PRICE_MAX)) : null,
            [FILTER_KEYS.IS_SELLING]: searchParams.get(FILTER_KEYS.IS_SELLING) === 'true',
            [FILTER_KEYS.CONDITION]: searchParams.get(FILTER_KEYS.CONDITION) || '',
        };
        initializeFilters(initialFilters);

        const sortParam = searchParams.get(FILTER_KEYS.SORT);
        if (sortParam) {
            initializeSort(sortParam);
        }
    }, [location.search, initializeFilters, initializeSort]);

    // 상태 -> URL 파라미터 동기화
    const [isInitialized, setIsInitialized] = useState(false);
    useEffect(() => {
        if (!isInitialized) {
            setIsInitialized(true);
            return;
        }
        const params = new URLSearchParams();
        if (filters[FILTER_KEYS.GENDER]) params.set(FILTER_KEYS.GENDER, filters[FILTER_KEYS.GENDER]);
        if (filters[FILTER_KEYS.SUBCATEGORY_IDS] && filters[FILTER_KEYS.SUBCATEGORY_IDS].length) {
            params.set(FILTER_KEYS.SUBCATEGORY_IDS, filters[FILTER_KEYS.SUBCATEGORY_IDS].join(','));
        }
        if (filters[FILTER_KEYS.BRANDS] && filters[FILTER_KEYS.BRANDS].length) {
            const names = filters[FILTER_KEYS.BRANDS].map(b => (typeof b === 'string' ? b : b.name));
            params.set(FILTER_KEYS.BRANDS, names.join(','));
        }
        if (filters[FILTER_KEYS.PRICE_MIN] != null) params.set(FILTER_KEYS.PRICE_MIN, filters[FILTER_KEYS.PRICE_MIN]);
        if (filters[FILTER_KEYS.PRICE_MAX] != null) params.set(FILTER_KEYS.PRICE_MAX, filters[FILTER_KEYS.PRICE_MAX]);
        if (filters[FILTER_KEYS.IS_SELLING]) params.set(FILTER_KEYS.IS_SELLING, 'true');
        if (filters[FILTER_KEYS.CONDITION]) params.set(FILTER_KEYS.CONDITION, filters[FILTER_KEYS.CONDITION]);
        if (selectedSort) params.set(FILTER_KEYS.SORT, selectedSort);

        navigate(`${location.pathname}?${params.toString()}`, { replace: true });

    }, [filters, selectedSort, navigate, location.pathname, isInitialized]);

    // 이벤트 핸들러
    const handleSortChange = (sortCode) => setSort(sortCode);

    const handleRemoveFilter = (filterType, value) => {
        switch (filterType) {
            case FILTER_KEYS.GENDER:
                updateFilter(FILTER_KEYS.GENDER, '');
                break;
            case FILTER_KEYS.SUBCATEGORY_IDS:
                updateFilter(FILTER_KEYS.SUBCATEGORY_IDS, filters[FILTER_KEYS.SUBCATEGORY_IDS].filter(id => id !== value));
                break;
            case FILTER_KEYS.BRANDS:
                updateFilter(FILTER_KEYS.BRANDS, filters[FILTER_KEYS.BRANDS].filter(brand => {
                    const brandName = typeof brand === 'string' ? brand : brand.name;
                    const valueName = typeof value === 'string' ? value : value.name;
                    return brandName !== valueName;
                }));
                break;
            case 'price': // price는 min/max 두 개라 상수가 없음
                updateFilter(FILTER_KEYS.PRICE_MIN, null);
                updateFilter(FILTER_KEYS.PRICE_MAX, null);
                break;
            case FILTER_KEYS.IS_SELLING:
                updateFilter(FILTER_KEYS.IS_SELLING, false);
                break;
            case FILTER_KEYS.CONDITION:
                updateFilter(FILTER_KEYS.CONDITION, '');
                break;
            default:
                break;
        }
    };

    const handleClearAllFilters = () => {
        clearAllFilters();
        setSort('POPULAR');
    };

    return {
        keyword,
        categoryName,
        filters,
        selectedSort,
        handleSortChange,
        handleRemoveFilter,
        handleClearAllFilters,
    };
}